'use client';

import React from 'react';
// import dynamic from 'next/dynamic';
import styles from './HomePage.module.css';
import { CountryConfig } from '../../lib/region/getCountrySpecificConfig';
import { productMap_home } from '../../content/products/uhHome';
import BuySection from './HomeSelectionSection';
// import { TitleBar } from 'components/ring-air/ProductSelectionSection/TitleBar';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { ProductCarouselItem } from 'components/shared/ProductCarousel';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useHomeContext } from './HomeContext';

const PageContainer = ({ children }: { children: React.ReactNode }) => (
  <div
    className={styles.pageContainer}
    style={
      {
        ['--home-pt']: isMarketingBannerActive() ? '80px' : '48px',
      } as React.CSSProperties
    }
  >
    {children}
  </div>
);

const ProductContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.productContainer}>{children}</div>
);

// Define component props
interface HomePageProps {
  countryCode?: string;
  countrySpecificConfig: CountryConfig;
}

/**
 * Main XHomePage component
 * Handles country-specific rendering and core product display
 */

const Title = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <span className={className ? `${styles.title} ${className}` : styles.title}>
    {children}
  </span>
);

export const HomePage: React.FC<HomePageProps> = ({
  countrySpecificConfig,
}) => {
  // Get product metadata
  const product = productMap_home;

  // HomeContext and locale are used by child sections; JSON-LD is handled at the page level
  useHomeContext();

  const carouselItems: ProductCarouselItem[] = [
    {
      videoSrc: getCompressedAssetUrl(
        '/web_v2/uh-home-v2/buy-page-carousel-v2/lg/vid-1-v2.mp4',
      ),
      videoSrcResponsive: getCompressedAssetUrl(
        '/web_v2/uh-home-v2/buy-page-carousel-v2/sm/vid-1-v2.mp4',
      ),
      poster: getCompressedAssetUrl(
        '/web_v2/uh-home-v2/buy-page-carousel-v2/lg/1.png',
      ),
      posterResponsive: getCompressedAssetUrl(
        '/web_v2/uh-home-v2/buy-page-carousel-v2/sm/1.png',
      ),
      autoSlideDuration: 7.1,
      background: '#000000',
      alt: '',
      title: (
        <Title className="light">
          Understand the home health <b>markers</b> that <b>impact</b> how you
          and your family <b>sleep</b>.
        </Title>
      ),
      textPosition: 'top-center',
      objectFit: 'cover',
    },

    // {
    //   src: 'web_v2/uh-home-v2/buy-page-carousel-v2/lg/1.png',
    //   srcResponsive: 'web_v2/uh-home-v2/buy-page-carousel-v2/sm/1.png',
    //   thumb: 'web_v2/home/buy/desktop/pricing_carousel_b.png/w_75,h_48',
    //   alt: '',
    //   title: (
    //     <Title>
    //       <b>Ultrahuman Home</b> tracks ambient markers to improve your{' '}
    //       <b>family&apos;s sleep quality — one device for all</b>.
    //     </Title>
    //   ),
    //   textPosition: 'top-center',
    //   objectFit: 'contain',
    // },

    {
      videoSrc: getCompressedAssetUrl(
        '/web_v2/uh-home-v2/buy-page-carousel-v2/lg/vid-2.mp4',
      ),
      videoSrcResponsive: getCompressedAssetUrl(
        '/web_v2/uh-home-v2/buy-page-carousel-v2/sm/vid-2.mp4',
      ),
      poster: 'web_v2/home/buy/desktop/carousel_lifestyle.jpg/w_75,h_48',
      posterResponsive:
        'web_v2/home/buy/desktop/carousel_lifestyle.jpg/w_75,h_48',

      alt: '',
      title: (
        <Title>
          Wake up to <b>comprehensive</b>, <b>actionable</b> insights with{' '}
          <b>sleep reports</b>.
        </Title>
      ),
      textPosition: 'top-center',
      objectFit: 'cover',
    },

    {
      src: 'web_v2/uh-home-v2/buy-page-carousel-v2/lg/2.png',
      srcResponsive: 'web_v2/uh-home-v2/buy-page-carousel-v2/sm/2.png',
      thumb: 'web_v2/home/buy/desktop/pricing_carousel_c.png/w_75,h_48',
      alt: '',
      objectPositionBottom: true,
      title: (
        <Title>
          Decode the <b>subtle shifts</b> in your sleep environment that occur
          while you rest.
        </Title>
      ),
      textPosition: 'middle-left',
      objectFit: 'contain',
    },

    {
      src: 'web_v2/uh-home-v2/buy-page-carousel-v2/lg/3.png',
      srcResponsive: 'web_v2/uh-home-v2/buy-page-carousel-v2/sm/3.png',
      thumb: 'web_v2/home/buy/desktop/carousel_lifestyle.jpg/w_75,h_48',
      alt: '',
      title: (
        <Title>
          Monitor <b>snoring</b> and <b>breathing</b> patterns to spot early
          signs of respiratory issues.
        </Title>
      ),
      textPosition: 'top-center',
      objectFit: 'contain',
    },

    {
      src: 'web_v2/uh-home-v2/buy-page-carousel-v2/lg/5-v2.png',
      srcResponsive: 'web_v2/uh-home-v2/buy-page-carousel-v2/sm/5-v2.png',
      thumb: 'web_v2/home/buy/desktop/pricing_carousel_aa.png/w_75,h_48',
      alt: '',
      title: (
        <Title>
          <b>HIPAA</b> and <b>GDPR</b> compliant to protect your data with
          complete security and privacy.
        </Title>
      ),
      textPosition: 'middle-right',
      objectFit: 'cover',
    },

    {
      src: 'web_v2/uh-home-v2/buy-page-carousel-v2/lg/5-v4.png',
      srcResponsive: 'web_v2/uh-home-v2/buy-page-carousel-v2/sm/5-v5.png',
      thumb: 'web_v2/home/buy/desktop/pricing_carousel_aa.png/w_75,h_48',
      alt: '',
      title: (
        <Title className="light">
          Control your sleep environment with smart home integrations using{' '}
          <b>Matter Protocol</b>.
        </Title>
      ),
      textPosition: 'top-center',
      objectFit: 'cover',
    },
    {
      src: 'web_v2/uh-home-v2/buy-page-carousel-v2/lg/6.png',
      srcResponsive: 'web_v2/uh-home-v2/buy-page-carousel-v2/sm/6.png',
      thumb: 'web_v2/home/buy/desktop/pricing_carousel_aa.png/w_75,h_48',
      alt: '',
      textPosition: 'top-center',
      objectFit: 'cover',
    },
  ];

  return (
    <PageContainer>
      {/* <TitleBar title="The world's most advanced ambient sleep monitor" /> */}
      {/* Main product container */}
      <ProductContainer>
        <BuySection
          product={product}
          carouselItems={carouselItems}
          contentTitle="Home"
          contentText=""
          ctaText="Learn More"
          // onCtaClick={() => console.log('CTA clicked')}
          countrySpecificConfigData={countrySpecificConfig}
        />
      </ProductContainer>
      {/* Render custom footer if provided for this country */}
      {/* {CustomFooter && <CustomFooter />} */}

      {/* Cookie banner for EU regions */}
      {countrySpecificConfig.showEuCookieBanner && (
        <div id="eu-cookie-banner" className="cookie-banner">
          {/* This would be implemented as a separate component */}
        </div>
      )}
    </PageContainer>
  );
};

export default HomePage;
